import * as React from 'react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import { Box, Typography, Tooltip, Divider } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Container from '@mui/material/Container';
import Copyright from '../../components/Copyright';
import GoogleLoginButton from '../../components/GoogleLoginButton';

import { AuthContext } from '../../context/AuthContext';
import { httpRequest } from '../../api/helper';
import { useNotification } from '../../context/NotificationContext';
import { API_ERROR_MESSAGE, AUTH_URLS } from '../../common/AppConstants';
import * as AppUrls from '../../common/AppUrls';
import { Helmet } from 'react-helmet';
import logger from '../../logger';

export default function SignUp() {
  const { loggedIn, login } = React.useContext(AuthContext);
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [otpToken, setOtpToken] = useState('');
  const [isOtpLoading, setIsOtpLoading] = useState(false);
  const [isSignupLoading, setIsSignupLoading] = useState(false);
  const [editErrors, setEditErrors] = useState({});

  const resetAllErrors = () => {
    setEditErrors({});
  };

  useEffect(() => {
    resetAllErrors();
  }, []);

  useEffect(() => {
    if (loggedIn) {
      navigate(AppUrls.DASHBOARD_URL);
    }
  }, [loggedIn, navigate]);

  const handleChange = (fieldAttr, value) => {
    // Clear error when user edits the field
    setEditErrors((prevErrors) => ({
      ...prevErrors,
      [fieldAttr]: undefined,
    }));
  };

  const sendOtp = async (event) => {
    event.preventDefault();
    setIsOtpLoading(true); // Disable otp button
    resetAllErrors();
    // const form_data = new FormData(event.currentTarget);

    // Send the otp for email verification
    const data = {
      email: email,
      terms_accepted: isTermsAccepted,
    };

    await httpRequest({
      method: 'POST',
      url: 'user/email-otp/',
      data: data,
      onSuccess: (data) => {
        setOtpToken(data.otp_token);
        addNotification('OTP is sent on email', 'info');
        setStep(2);
      },
      onFailure: (data) => {
        setEditErrors(data);
      },
      addNotification,
    });

    setIsOtpLoading(false);
  };

  const handleSignUp = async (event) => {
    event.preventDefault();
    const form_data = new FormData(event.currentTarget);

    // Client-side check: passwords must match
    if (form_data.get('password') !== form_data.get('confirm_password')) {
      setEditErrors((prev) => ({
        ...prev,
        confirm_password: ["Passwords don't match"],
      }));
      return;
    }

    setIsSignupLoading(true); // Disable signup button
    resetAllErrors();

    // Perform sign-up logic, hit the API to complete sign up
    const data = {
      event_type: "signup",
      email: email,
      first_name: form_data.get('first_name'),
      last_name: form_data.get('last_name'),
      password: form_data.get('password'),
      otp: form_data.get('otp'),
      terms_accepted: isTermsAccepted,
      otp_token: otpToken,
    };

    await httpRequest({
      method: 'POST',
      url: AUTH_URLS.EMAIL_LOGIN,
      data: data,
      onSuccess: (data) => {
        login(data.access, data.refresh);
        navigate(AppUrls.DASHBOARD_URL);
      },
      onFailure: (data) => {
        setEditErrors(data);
      },
      addNotification,
    });

    setIsSignupLoading(false);
  };

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex" />
      </Helmet>
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: 2,
            mt: 16,
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography
            variant="h1"
            color="primary.main"
            sx={{ mb: 6 }}
          >
            Sign up
          </Typography>

          {/* Social Login */}
          {step === 1 && (
            <Grid container>
              <Grid item xs sx={{ textAlign: 'center', mt: 2 }}>
                <GoogleLoginButton event_type="signup" />
              </Grid>
              {/* <Grid item xs sx={{ textAlign: 'center', mt: 1 }}>
                <FacebookLoginButton event_type="signup" />
              </Grid> */}
            </Grid>
          )}

          {/* Divider with Or */}
          {step === 1 && (
            <Grid container>
              <Grid item xs>
                <Box sx={{ textAlign: 'center'}}>
                  <Divider sx={{ mt: 4, mb: 1 }}>
                    <Typography variant="body1" color="text.secondary">
                      or
                    </Typography>
                  </Divider>
                </Box>
                </Grid>
            </Grid>
          )}
          
          {/* Email/Password Based */}
          <Box
            component="form"
            noValidate
            onSubmit={handleSignUp}
            sx={{ mt: 2 }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  onChange={(e) => {
                    setEmail(e.target.value);
                    handleChange('email', e.target.value);
                  }}
                  autoFocus
                  disabled={step === 2 || isOtpLoading}
                  error={Boolean(editErrors.email)}
                  helperText={editErrors.email?.[0] || ''}
                />
              </Grid>
              {step === 2 && (
                <>
                  <Grid item xs={12}>
                    <Tooltip title='Please check your email inbox for the OTP' arrow>
                      <TextField
                        required
                        fullWidth
                        name="otp"
                        label="OTP"
                        type="password"
                        id="otp"
                        autoFocus
                        onChange={(e) => handleChange('otp', e.target.value)}
                        error={Boolean(editErrors.otp)}
                        helperText={editErrors.otp?.[0] || ''}
                      />
                    </Tooltip>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      autoComplete="given-name"
                      name="first_name"
                      required
                      fullWidth
                      id="first_name"
                      label="First Name"
                      onChange={(e) =>
                        handleChange('first_name', e.target.value)
                      }
                      error={Boolean(editErrors.first_name)}
                      helperText={editErrors.first_name?.[0] || ''}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      id="last_name"
                      label="Last Name"
                      name="last_name"
                      autoComplete="family-name"
                      onChange={(e) =>
                        handleChange('last_name', e.target.value)
                      }
                      error={Boolean(editErrors.last_name)}
                      helperText={editErrors.last_name?.[0] || ''}
                    />
                  </Grid>
                  <Grid item xs={12}>
                  <Tooltip
                    title={
                      <Box sx={{ textAlign: 'left' }}>
                        <Typography variant="body2">Your password must:</Typography>
                        <Box component="ul" sx={{ pl: 2, mt: 0.5, mb: 0.5 }}>
                          <Typography component="li" variant="body2">Be at least 12 characters long</Typography>
                          <Typography component="li" variant="body2">Include at least 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special character (@$!%*?&)</Typography>
                          <Typography component="li" variant="body2">Not be a commonly used password</Typography>
                          <Typography component="li" variant="body2">Not be too similar to your personal information</Typography>
                        </Box>
                      </Box>
                    }
                    // placement="top"
                    // arrow
                  >
                      <TextField
                        required
                        fullWidth
                        name="password"
                        label="New Password"
                        type="password"
                        id="password"
                        autoComplete="new-password"
                        onChange={(e) => handleChange('password', e.target.value)}
                        error={Boolean(editErrors.password)}
                        helperText={editErrors.password?.[0] || ''}
                      />
                    </Tooltip>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      fullWidth
                      name="confirm_password"
                      label="Confirm Password"
                      type="password"
                      id="confirm_password"
                      autoComplete="confirm-new-password"
                      onChange={(e) =>
                        handleChange('confirm_password', e.target.value)
                      }
                      error={Boolean(editErrors.confirm_password)}
                      helperText={editErrors.confirm_password?.[0] || ''}
                    />
                  </Grid>
                  {/* <Grid item xs={12}>
                    <FormControlLabel
                      control={<Checkbox value="allowExtraEmails" color="primary" />}
                      label="I want to receive inspiration, marketing promotions and updates via email."
                    />
                  </Grid> */}
                </>
              )}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={isTermsAccepted}
                      onChange={(e) => setIsTermsAccepted(e.target.checked)}
                    />
                  }
                  label={
                    <>
                      I agree to the{' '}
                      <Link
                        href="/terms-of-service"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Terms of Service
                      </Link>{' '}
                      and{' '}
                      <Link
                        href="/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Privacy Policy
                      </Link>
                      .
                    </>
                  }
                />
              </Grid>
              {step === 1 && (
                <Grid item xs={12}>
                  <Tooltip
                    title={!isTermsAccepted ? 'Please accept the terms & conditions to continue' : ''}
                    disableHoverListener={isTermsAccepted}
                    // arrow
                  >
                    <span>
                      <Button
                        type="button"
                        onClick={sendOtp}
                        fullWidth
                        variant="contained"
                        sx={{ mt: 3, mb: 2 }}
                        disabled={!isTermsAccepted || isOtpLoading}
                      >
                        {isOtpLoading ? 'Sending OTP' : 'Proceed'}
                      </Button>
                    </span>
                  </Tooltip>
                </Grid>
              )}
              {step === 2 && (
                <Grid item xs={12}>
                  <Tooltip
                    title={!isTermsAccepted ? 'Please accept the terms & conditions to continue' : ''}
                    disableHoverListener={isTermsAccepted}
                    // arrow
                  >
                    <span>
                      <Button
                        type="submit"
                        // onClick={handleSignUp}
                        fullWidth
                        variant="contained"
                        sx={{ mt: 3, mb: 2 }}
                        disabled={!isTermsAccepted || isSignupLoading}
                      >
                        Sign Up
                      </Button>
                    </span>
                  </Tooltip>
                </Grid>
              )}
            </Grid>

            {/* Sign In Link */}
            <Grid container justifyContent="center" sx={{ mt: 1 }}>
              <Grid item
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                }}
              >
                <Typography variant="body1" color="primary.main" gutterBottom>
                  Have an account Already?
                </Typography>
                <Link href="/sign-in" variant="h4" gutterBottom>
                  Sign in
                </Link>
              </Grid>
            </Grid>
          </Box>
        </Box>
        <Copyright sx={{ mt: 8 }} />
      </Container>
    </>
  );
}
